import { Component } from '@theme/component';

/**
 * <product-share> — share row on the product page
 * (blocks/custom-product-share.liquid): WhatsApp (a plain wa.me link, works
 * without JS), Copy link, and the device's native share sheet where the
 * browser supports it (phones). The native button is only revealed when
 * `navigator.share` exists, so no shopper sees a button that does nothing.
 *
 * data-url, data-title; data-label-copied (feedback text)
 * refs: native, copy, copyLabel, status (live region)
 *
 * @extends {Component<{ native: HTMLButtonElement, copy: HTMLButtonElement, copyLabel: HTMLElement, status: HTMLElement }>}
 */
class ProductShare extends Component {
  requiredRefs = ['native', 'copy', 'copyLabel', 'status'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #reset;

  connectedCallback() {
    super.connectedCallback();
    this.refs.native.hidden = typeof navigator.share !== 'function';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#reset);
  }

  async shareNative() {
    try {
      await navigator.share({ title: this.dataset.title, url: this.dataset.url });
    } catch {
      // Shopper closed the sheet: nothing to do
    }
  }

  async copyLink() {
    const { copy, copyLabel, status } = this.refs;
    const original = copyLabel.textContent;
    try {
      await navigator.clipboard.writeText(this.dataset.url ?? location.href);
    } catch {
      return;
    }
    const copied = this.dataset.labelCopied ?? '';
    copyLabel.textContent = copied;
    status.textContent = copied;
    copy.setAttribute('data-copied', '');
    clearTimeout(this.#reset);
    this.#reset = setTimeout(() => {
      copyLabel.textContent = original;
      copy.removeAttribute('data-copied');
    }, 2000);
  }
}

if (!customElements.get('product-share')) {
  customElements.define('product-share', ProductShare);
}
